import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import i18n, { SUPPORTED_LANGUAGES } from "@/lib/i18n";

const HotelContext = createContext(null);
const CTX_KEY = "aura_hotel_ctx";

/**
 * Applies the branding as CSS custom properties on <html> so any component
 * using `var(--brand-primary)` or `var(--brand-accent)` picks it up.
 * Falls back to Aura's sage/cream defaults when there's no hotel branding.
 */
function applyBranding(branding) {
  const root = document.documentElement;
  const defaults = {
    "--brand-primary": "#3A5141",
    "--brand-accent": "#EADDCB",
    "--brand-font-serif": "Cormorant Garamond, serif",
    "--brand-font-sans": "Manrope, sans-serif",
  };
  root.style.setProperty("--brand-primary", branding?.primary_color || defaults["--brand-primary"]);
  root.style.setProperty("--brand-accent", branding?.accent_color || defaults["--brand-accent"]);
  root.style.setProperty("--brand-font-serif",
    branding?.font_serif ? `"${branding.font_serif}", serif` : defaults["--brand-font-serif"]);
  root.style.setProperty("--brand-font-sans",
    branding?.font_sans ? `"${branding.font_sans}", sans-serif` : defaults["--brand-font-sans"]);
}

const SUPPORTED_CODES = new Set(SUPPORTED_LANGUAGES.map((l) => l.code));
const LANG_MANUAL_KEY = "aura_lang_manual"; // set to "1" by LanguageSwitcher

/**
 * Auto-switch the UI language to the hotel country's default. We only do
 * this once per QR arrival, and only when the user hasn't manually picked
 * another language in this browser (flag lives in localStorage under
 * `aura_lang_manual`). Guests who prefer French keep French even at a
 * Japanese property.
 */
function applyLocale(locale) {
  const lang = locale?.default_language;
  if (!lang || !SUPPORTED_CODES.has(lang)) return;
  if (localStorage.getItem(LANG_MANUAL_KEY) === "1") return;
  if (i18n.resolvedLanguage === lang) return;
  i18n.changeLanguage(lang);
}

/**
 * Room context lives in localStorage under `aura_hotel_ctx` and holds:
 *   { hotel, room, branding, locale, session_id, expires_at }
 * The `session_id` is a RoomServiceSession — used later when the
 * authenticated patient confirms their stay. `session_id` may be null when
 * the context was primed via the legacy `?qr=` param (no session was minted).
 *
 * `locale` is a country-derived block sourced from `country_configs`:
 *   { country_code, currency, iana_tz, default_language, emergency_number }
 * Consumed by useLocale() so guest-facing pages render prices and times in
 * the property's country.
 */
export function HotelProvider({ children }) {
  const [ctx, setCtxState] = useState(() => {
    const raw = localStorage.getItem(CTX_KEY);
    return raw ? JSON.parse(raw) : null;
  });

  useEffect(() => {
    applyBranding(ctx?.branding);
    applyLocale(ctx?.locale);
  }, [ctx]);

  useEffect(() => {
    // Legacy fallback: `/?qr=<code>` still works for previously printed cards.
    const params = new URLSearchParams(window.location.search);
    const qr = params.get("qr");
    if (!qr) return;
    api
      .get(`/hotels/by-qr/${qr}`)
      .then((r) => {
        setCtx({
          hotel: r.data.hotel,
          room: r.data.room,
          branding: r.data.branding,
          locale: r.data.locale || null,
          session_id: null,   // legacy path — no session was minted
        });
      })
      .catch(() => {});
  }, []);

  const setCtx = (next) => {
    if (!next) {
      localStorage.removeItem(CTX_KEY);
      setCtxState(null);
      applyBranding(null);
      return;
    }
    localStorage.setItem(CTX_KEY, JSON.stringify(next));
    setCtxState(next);
  };

  const clear = () => setCtx(null);

  return (
    <HotelContext.Provider value={{ ctx, setCtx, clear }}>
      {children}
    </HotelContext.Provider>
  );
}

export const useHotel = () => useContext(HotelContext);
